import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useChanged } from './useChanged';
function insertChunk(list, item) {
    const output = [];
    let currentPos = 0;
    let currentOffset = 0;
    // 1. Traverse until the target offset is reached. If target offset is
    // reached, we have to slice the chunk to fit the new entry.
    //             v cut
    // ----####------#######
    //             #####
    while (currentPos < list.length) {
        const chunk = list[currentPos];
        // NOTE There is an invariant property: List of chunks must be continious.
        // That is, while we allow 'empty' chunks, there must be 'empty' chunk
        // explaining the count and offset of the chunk. There must be no 'gaps',
        // i.e. sudden jump of offset.
        if (chunk.count + chunk.offset >= item.offset) {
            if (chunk.offset >= item.offset) {
                // It immediately reaches threshold at the beginning - don't
                // insert anything.
                break;
            }
            const remaining = item.offset - chunk.offset;
            // Slice the output to meet the criteria.
            currentOffset = chunk.offset + remaining;
            if (chunk.type === 'page') {
                output.push({
                    ...chunk,
                    count: remaining,
                    items: chunk.items.slice(0, remaining),
                });
            }
            else {
                output.push({
                    ...chunk,
                    count: remaining,
                });
            }
            break;
        }
        // It does not reach any threshold; just output them.
        output.push(chunk);
        currentPos += 1;
        currentOffset = chunk.offset + chunk.count;
    }
    // 1-2. If it hasn't reached any threshold yet, insert a empty chunk to
    // reach the target offset.
    if (currentOffset !== item.offset) {
        output.push({
            type: 'empty',
            offset: currentOffset,
            count: item.offset - currentOffset,
        });
    }
    // 2. Insert the new item to the output.
    output.push(item);
    // 3. Insert the remaining chunks, while slicing / skipping when
    // necessary.
    const exitOffset = item.offset + item.count;
    for (; currentPos < list.length; currentPos += 1) {
        const chunk = list[currentPos];
        if (chunk.offset < exitOffset) {
            if (chunk.offset + chunk.count <= exitOffset) {
                // It immediately reaches threshold at the beginning - don't
                // insert anything.
                continue;
            }
            const remaining = exitOffset - chunk.offset;
            // Slice the output to meet the criteria.
            if (chunk.type === 'page') {
                output.push({
                    ...chunk,
                    offset: exitOffset,
                    count: chunk.count - remaining,
                    items: chunk.items.slice(remaining),
                });
            }
            else {
                output.push({
                    ...chunk,
                    offset: exitOffset,
                    count: chunk.count - remaining,
                });
            }
            continue;
        }
        // It does not reach any threshold; just output them.
        output.push(chunk);
    }
    return output;
}
function mapHintChunks(chunks) {
    // eslint-disable-next-line array-callback-return
    return chunks.map((v) => {
        // eslint-disable-next-line default-case
        switch (v.type) {
            case 'page':
                return { type: 'page', page: v.page, count: v.count };
            case 'empty':
                return { type: 'empty', count: v.count };
        }
    });
}
function getChunkIndex(chunks, offset) {
    // Run a binary search to find the right chunk.
    let minPos = 0;
    let maxPos = chunks.length - 1;
    while (minPos <= maxPos) {
        const middle = (minPos + maxPos) >> 1;
        const middleItem = chunks[middle];
        if (middleItem.offset > offset) {
            maxPos = middle - 1;
        }
        else if (middleItem.offset + middleItem.count <= offset) {
            minPos = middle + 1;
        }
        else {
            // Found the right item.
            return middle;
        }
    }
    return -1;
}
function getLoadablePage(state, per, totalCount, targetStartIndex, targetEndIndex) {
    // Determine which page to load. This can be determined using:
    // 1. Using nearest loaded page. This can be used when nearest page is
    //    not far enough (<20 items)
    // 2. Using hint chunks. This can be used when hint chunks covers that
    //    range.
    // 3. Using heuristics. Since we can *usually* assume that pages are divided
    //    by 'per' count, we can skip from nearest loaded page.
    // Note that load request doesn't have to cover all items between
    // startIndex and endIndex - it's fine to load just a part of it -
    // loadMore will be triggered again anyway.
    // Note that endIndex is inclusive; [0, 0] means 0th item must be loaded.
    const startIndex = Math.max(0, targetStartIndex);
    let endIndex = targetEndIndex;
    if (totalCount != null) {
        endIndex = Math.min(endIndex, totalCount - 1);
    }
    if (state.totalCount != null) {
        endIndex = Math.min(endIndex, state.totalCount - 1);
    }
    if ((totalCount != null && startIndex > totalCount) ||
        (state.totalCount != null && startIndex > state.totalCount)) {
        return null;
    }
    let lastOffset = startIndex;
    // Retrieve the first page to load. If there is no page left to load,
    // do nothing.
    let chunkIndex = getChunkIndex(state.chunks, startIndex);
    let chunkPage = null;
    let chunkOffset = null;
    // Scroll until the end is reached.
    if (chunkIndex !== -1) {
        while (chunkIndex < state.chunks.length && lastOffset <= endIndex) {
            const chunkItem = state.chunks[chunkIndex];
            // Empty item immediately fails the test.
            if (chunkItem.type === 'empty') {
                // This assumes that 'empty' chunk item is not consecutive.
                // Determine which page to load. It can:
                // 1. Snap to the start of empty chunk (or end of page chunk)
                // 2. Snap to the end of empty chunk (or start of page chunk)
                // 3. Snap to neither
                // For 1 or 2 to succeed, it must be within 'per' range.
                //
                // |-per-............-per-|
                // |...---here---.........|
                //
                // For the above case, it'll snap to start of chunk because it's
                // within 'per' radius.
                //
                // |-per-............-per-|
                // |.....---here---.......|
                //
                // This does not snap, therefore heuristics will be used. (Note, this
                // may generate fragmentation or other data integrity errors)
                const prevItem = state.chunks[chunkIndex - 1];
                const nextItem = state.chunks[chunkIndex + 1];
                // Check leftmost
                if (prevItem != null && prevItem.type === 'page' &&
                    startIndex <= chunkItem.offset + per) {
                    chunkPage = prevItem.page + 1;
                    chunkOffset = chunkItem.offset;
                    break;
                }
                // Check rightmost
                if (nextItem != null && nextItem.type === 'page' &&
                    endIndex >= chunkItem.offset + chunkItem.count - per) {
                    chunkPage = nextItem.page - 1;
                    chunkOffset = chunkItem.offset + chunkItem.count - per;
                    break;
                }
                // Nothing matches; use heuristics
                lastOffset = Math.max(startIndex, chunkItem.offset);
                break;
            }
            else if (chunkItem.type === 'page' &&
                chunkIndex === state.chunks.length - 1) {
                // Last page has reached; since startIndex already intersects with
                // the pages, just load next page.
                chunkPage = chunkItem.page + 1;
                chunkOffset = chunkItem.offset + chunkItem.count;
            }
            lastOffset = chunkItem.offset + chunkItem.count;
            chunkIndex += 1;
        }
    }
    else {
        // Nothing matched! ... If last chunk is page chunk, and startIndex is
        // within bounds, use that.
        const lastItem = state.chunks[state.chunks.length - 1];
        if (lastItem != null && lastItem.type === 'page' &&
            endIndex <= lastItem.offset + lastItem.count + per) {
            chunkPage = lastItem.page + 1;
            chunkOffset = lastItem.offset + lastItem.count;
        }
    }
    if (lastOffset > endIndex) {
        return null;
    }
    // Check if page number is determined yet...
    if (chunkPage == null || chunkOffset == null) {
        // It still didn't match. It can use hint information to resurrect
        // the given page, or if even that doesn't exist, heuristics can be used.
        // While we store hint chunks, heuristics are pretty reliable for now -
        // there is no need for hint chunk implementation for yet.
        chunkPage = Math.floor(lastOffset / per) + 1;
        chunkOffset = (chunkPage - 1) * per;
    }
    return { page: chunkPage, offset: chunkOffset };
}
function initPagedInfiniteListState(chunk) {
    if (chunk != null) {
        const chunks = insertChunk([], {
            type: 'page',
            page: chunk.page,
            offset: chunk.offset ?? 0,
            count: chunk.items.length,
            items: chunk.items,
        });
        const hasNext = chunk.hasNext != null
            ? chunk.hasNext
            : chunk.items.length > 0;
        const afterOffset = (chunk.offset ?? 0) + chunk.items.length;
        return {
            chunks,
            hintChunks: mapHintChunks(chunks),
            count: afterOffset,
            scrolledCount: null,
            totalCount: !hasNext ? afterOffset : null,
            epoch: 0,
            error: null,
        };
    }
    return {
        chunks: [],
        hintChunks: [],
        count: 0,
        scrolledCount: null,
        totalCount: null,
        epoch: 0,
        error: null,
    };
}
function pagedInfiniteListReducer(state, action) {
    // eslint-disable-next-line default-case
    switch (action.type) {
        case 'load': {
            // Try to insert the chunk information at the designated offset.
            // If there is any empty space between chunks, insert 'empty' chunk
            // between it.
            // If target offset is already occupied, overwrite over the chunk.
            // TODO Remove any duplicate items if possible to do so
            const { offset, page, items, hasNext } = action.item;
            const targetOffset = action.targetOffset != null
                ? action.targetOffset
                : offset;
            const outputChunks = insertChunk(state.chunks, {
                type: 'page',
                page,
                offset: targetOffset ?? 0,
                count: items.length,
                items,
            });
            const lastChunk = outputChunks[outputChunks.length - 1];
            let nextTotalCount = state.totalCount;
            if (hasNext != null ? !hasNext : items.length === 0) {
                const offset = lastChunk.offset + lastChunk.count;
                nextTotalCount = nextTotalCount != null
                    ? Math.min(nextTotalCount, offset)
                    : offset;
            }
            return {
                ...state,
                chunks: outputChunks,
                hintChunks: mapHintChunks(outputChunks),
                count: lastChunk.offset + lastChunk.count,
                totalCount: nextTotalCount,
                epoch: action.isAsync ? state.epoch + 1 : state.epoch,
            };
        }
        case 'end': {
            return {
                ...state,
                totalCount: state.totalCount != null
                    ? Math.min(state.totalCount, action.offset)
                    : action.offset,
                epoch: action.isAsync ? state.epoch + 1 : state.epoch,
            };
        }
        case 'setHint': {
            return {
                ...state,
                hintChunks: action.hintChunks,
                scrolledCount: action.scrolledCount,
            };
        }
        case 'reset': {
            return initPagedInfiniteListState();
        }
        case 'error': {
            return { ...state, error: action.error };
        }
        case 'clearError': {
            return { ...state, error: null };
        }
    }
}
export function usePagedInfiniteList(tabId, pending, loadMore, getInitialData, per, loadThreshold = 5, 
/**
 * The absolute total count of the items passed from the server. The client
 * will not request any more pages further from this.
 * Note this can be omitted - most pages don't even have totalCount value.
 * When this is omitted, it may send additional requests and therefore
 * a placeholder 'flicker' might be visible when the end of the list is
 * reached.
 */
totalCount) {
    // TODO Reload when location changes
    const initialDataFilled = useRef(false);
    const initialRendered = useRef(false);
    const innerPending = useRef(false);
    const locationChanged = useChanged(tabId);
    const changedEpoch = useRef(0);
    const stateEpoch = useRef(0);
    const displayedRef = useRef([0, 0]);
    const mountedRef = useRef(true);
    const [state, dispatch] = useReducer(pagedInfiniteListReducer, undefined, () => initPagedInfiniteListState(getInitialData()));
    const stateRef = useRef(state);
    stateRef.current = state;
    if (locationChanged) {
        changedEpoch.current += 1;
        innerPending.current = false;
    }
    // Unmount handler
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        if (changedEpoch.current > 0) {
            dispatch({ type: 'reset' });
            initialDataFilled.current = false;
            initialRendered.current = false;
        }
        if (tabId != null) {
            // Reload hint data, if available.
            let value;
            try {
                value = JSON.parse(sessionStorage[`plist-hint-${tabId}`] || 'null');
            }
            catch (e) {
                // Do nothing if failed
            }
            if (value != null) {
                dispatch({
                    type: 'setHint',
                    hintChunks: value.hintChunks,
                    scrolledCount: value.scrolledCount,
                });
            }
        }
    }, [changedEpoch.current, tabId]);
    useEffect(() => {
        const prevEpoch = stateEpoch.current;
        if (stateEpoch.current !== state.epoch) {
            stateEpoch.current = state.epoch;
            if (prevEpoch < state.epoch && tabId != null) {
                const timerId = window.setTimeout(() => {
                    try {
                        sessionStorage[`plist-hint-${tabId}`] = JSON.stringify({
                            hintChunks: state.hintChunks,
                            scrolledCount: state.count,
                        });
                    }
                    catch (e) {
                        // Do nothing if failed
                    }
                }, 200);
                return () => window.clearTimeout(timerId);
            }
        }
    }, [state.epoch, tabId, state]);
    const handleGetItem = useMemo(() => {
        // To avoid running binary search every time an item lookup is needed, we
        // memoize last index of the item. This can reduce time complexity from
        // O(m log n) to O(m + log n), with space complexity of O(1).
        let currentItem = null;
        return (index) => {
            // If the index is out of bounds, return null immediately.
            if (index < 0 || index >= state.count) {
                return null;
            }
            // If the index is already within bounds, return the item.
            if (currentItem != null &&
                currentItem.offset <= index &&
                currentItem.offset + currentItem.count > index) {
                if (currentItem.type === 'page') {
                    return currentItem.items[index - currentItem.offset];
                }
                return null;
            }
            // Run a binary search to find the right chunk.
            const chunkIndex = getChunkIndex(state.chunks, index);
            if (chunkIndex === -1) {
                return null;
            }
            currentItem = state.chunks[chunkIndex];
            if (currentItem.type === 'page') {
                return currentItem.items[index - currentItem.offset];
            }
            return null;
        };
    }, [state]);
    const handleLoadMore = useCallback(async (startIndex, endIndex) => {
        // Reset the currently displayed index.
        displayedRef.current = [startIndex, endIndex];
        if (innerPending.current || pending || !initialDataFilled.current ||
            stateRef.current.error != null) {
            return;
        }
        let nextPage;
        const currentEpoch = changedEpoch.current;
        // Lock
        innerPending.current = true;
        do {
            if (!mountedRef.current ||
                stateRef.current.error != null ||
                changedEpoch.current !== currentEpoch) {
                break;
            }
            const [start, end] = displayedRef.current;
            nextPage =
                getLoadablePage(stateRef.current, per, totalCount, start, end + 1);
            // Digest remaining...
            // If there are anything to load, perform loading.
            if (nextPage == null) {
                break;
            }
            try {
                const result = await loadMore(nextPage.page);
                if (changedEpoch.current !== currentEpoch) {
                    break;
                }
                dispatch({
                    type: 'load',
                    item: {
                        ...result,
                        offset: result.offset || nextPage.offset,
                    },
                    isAsync: true,
                });
            }
            catch (e) {
                const error = e;
                // Handle error
                console.error(`${error.toString()}\n${error.stack}`);
                dispatch({ type: 'error', error });
            }
        } while (nextPage != null);
        // Unlock
        innerPending.current = false;
    }, [pending, loadMore, handleGetItem]);
    useEffect(() => {
        if (!pending) {
            if (!initialDataFilled.current) {
                const chunk = getInitialData();
                if (chunk != null) {
                    if (chunk.items.length === 0) {
                        dispatch({
                            type: 'end',
                            offset: chunk.offset ?? 0,
                            isAsync: true,
                        });
                    }
                    else {
                        dispatch({ type: 'load', item: chunk });
                    }
                    initialDataFilled.current = true;
                }
                else {
                    initialDataFilled.current = true;
                    initialRendered.current = true;
                    handleLoadMore(displayedRef.current[0], displayedRef.current[1]);
                }
            }
            else if (!initialRendered.current) {
                initialRendered.current = true;
                handleLoadMore(displayedRef.current[0], displayedRef.current[1]);
            }
        }
    }, [pending, getInitialData, handleLoadMore]);
    const handleRowsRendered = useCallback((startIndex, endIndex) => {
        handleLoadMore(startIndex - loadThreshold, endIndex + loadThreshold);
    }, [loadThreshold, handleLoadMore]);
    const reloadData = useCallback(async () => {
        changedEpoch.current += 1;
        dispatch({ type: 'reset' });
        await handleLoadMore(0, 1);
    }, [handleLoadMore]);
    const handleClearError = useCallback(() => {
        dispatch({ type: 'clearError' });
        handleLoadMore(displayedRef.current[0], displayedRef.current[1]);
    }, [handleLoadMore]);
    const count = Math.max([state.count + per, state.totalCount, totalCount]
        .reduce((p, v) => (v == null ? p : Math.min(v, p)), Infinity), state.scrolledCount ?? 0);
    const loadedCount = state.count;
    return {
        state,
        count: state.error != null ? loadedCount : count,
        loadedCount,
        getItem: handleGetItem,
        loadMore: handleLoadMore,
        handleRowsRendered,
        error: state.error,
        reloadData,
        clearError: handleClearError,
    };
}
//# sourceMappingURL=usePagedInfiniteList.js.map